'use client'

import { useState, useEffect } from 'react'
import { ArrowLeft, FileText, Save, User, Mail, Phone } from 'lucide-react'
import UrlPreview from './UrlPreview'
import { panel, panelInner, headingClass, mutedClass, ghostBtn, primaryBtn, primaryBtnInline, fieldClass, labelClass, errorBox, successBox } from './ui'

interface AdminBriefViewProps {
  briefId: string
  userId: string
  onBack: () => void
}

interface Manager {
  id: string
  name: string
  email: string
  phone: string | null
  isActive?: boolean
}

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'in_review', label: 'In Review' },
  { value: 'completed', label: 'Completed' },
  { value: 'on_hold', label: 'On Hold' },
]

export default function AdminBriefView({ briefId, userId, onBack }: AdminBriefViewProps) {
  const [brief, setBrief] = useState<any>(null)
  const [managers, setManagers] = useState<Manager[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [adminNotes, setAdminNotes] = useState('')
  const [selectedManagerId, setSelectedManagerId] = useState<string>('')
  const [selectedStatus, setSelectedStatus] = useState<string>('')

  useEffect(() => {
    loadData()
  }, [briefId, userId])

  const loadData = async () => {
    try {
      const [briefRes, managersRes] = await Promise.all([
        fetch(`/api/brief613/briefs/${briefId}`, {
          headers: {
            'x-user-id': userId,
            'x-user-role': 'admin',
          },
        }),
        fetch('/api/brief613/managers', {
          headers: {
            'x-user-id': userId,
            'x-user-role': 'admin',
          },
        }),
      ])

      if (!briefRes.ok || !managersRes.ok) {
        throw new Error('Failed to fetch data')
      }

      const contentType = briefRes.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON')
      }

      const briefData = await briefRes.json()
      const managersData = await managersRes.json()

      if (briefData.success) {
        setBrief(briefData.brief)
        setAdminNotes(briefData.brief.adminNotes || '')
        setSelectedStatus(briefData.brief.status || 'submitted')
        setSelectedManagerId(briefData.brief.managerId || '')
      }

      if (managersData.success) {
        setManagers(managersData.managers.filter((m: Manager) => m.isActive))
      }
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSaveAll = async () => {
    // Check if there are any changes
    const hasStatusChange = selectedStatus && selectedStatus !== brief?.status
    const hasManagerChange = selectedManagerId !== (brief?.managerId || '')
    const hasNotesChange = adminNotes !== (brief?.adminNotes || '')

    if (!hasStatusChange && !hasManagerChange && !hasNotesChange) {
      alert('No changes to save')
      return
    }

    setSaving(true)
    try {
      const requestBody: any = {}

      // Only include fields that have changed
      if (hasStatusChange) {
        requestBody.status = selectedStatus
      }

      if (hasManagerChange) {
        requestBody.manager_id = selectedManagerId || ''
      }

      if (hasNotesChange) {
        requestBody.admin_notes = adminNotes
      }

      console.log('[AdminBriefView] Saving all changes:', requestBody)

      const response = await fetch(`/api/brief613/briefs/${briefId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': userId,
          'x-user-role': 'admin',
        },
        body: JSON.stringify(requestBody),
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP error! status: ${response.status}, ${errorText}`)
      }

      const contentType = response.headers.get('content-type')
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON')
      }

      const data = await response.json()
      console.log('[AdminBriefView] Save response:', data)

      if (data.success && data.brief) {
        // Update local state immediately with the returned brief data
        setBrief(data.brief)
        setSelectedStatus(data.brief.status)
        setSelectedManagerId(data.brief.managerId || '')
        setAdminNotes(data.brief.adminNotes || '')
        
        // Build success message
        const changes: string[] = []
        if (hasStatusChange) changes.push(`Status: ${data.brief.status}`)
        if (hasManagerChange) changes.push(`Manager: ${data.brief.assignedManager?.name || 'None'}`)
        if (hasNotesChange) changes.push('Notes updated')
        
        alert(`Changes saved successfully! ${changes.join(', ')}`)
        // Reload data to get latest from server
        await loadData()
      } else {
        console.error('[AdminBriefView] Failed to save:', data.error)
        alert(data.error || 'Failed to save changes')
      }
    } catch (error: any) {
      console.error('Error saving changes:', error)
      alert(`Failed to save changes: ${error.message || 'Unknown error'}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={`${panel} p-8 max-w-4xl mx-auto text-center`}>
        <div className="w-12 h-12 border-4 border-white/20 border-t-white/80 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/55">Loading brief...</p>
      </div>
    )
  }

  if (!brief) {
    return (
      <div className={`${panel} p-8 max-w-4xl mx-auto text-center`}>
        <p className="text-white/55">Brief not found</p>
        <button onClick={onBack} className={ghostBtn}>
          Back
        </button>
      </div>
    )
  }

  const deliverables = brief.deliverables
    ? typeof brief.deliverables === 'string'
      ? JSON.parse(brief.deliverables)
      : brief.deliverables
    : []

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className={ghostBtn}>
          <ArrowLeft size={18} />
          Back to Dashboard
        </button>
        <span
          className={`px-3 py-1 text-xs font-semibold rounded-full border ${
            brief.status === 'submitted'
              ? 'bg-blue-500/10 text-blue-300/90 border-blue-500/20'
              : brief.status === 'in_progress'
              ? 'bg-yellow-500/10 text-yellow-300/90 border-yellow-500/20'
              : brief.status === 'completed'
              ? 'bg-emerald-500/10 text-emerald-300/90 border-emerald-500/20'
              : 'bg-white/5 text-white/50 border-white/10'
          }`}
        >
          {brief.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Brief Content */}
        <div className="lg:col-span-2 space-y-6">
          <div className={`${panel} p-8`}>
            <div className="flex items-center gap-3 mb-6">
              <FileText className="text-white/50" size={22} />
              <div>
                <h2 className="text-base font-semibold text-white/90 sm:text-lg">Creative Brief</h2>
                <p className="text-white/70">
                  {brief.companyProfile?.companyName || 'No company'} • Created{' '}
                  {new Date(brief.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Brief Type */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Brief Type</h3>
                <p className="text-white/90 capitalize">{brief.briefType ? brief.briefType.replace('_', ' ') : 'N/A'}</p>
              </div>

              {/* Campaign Title */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Campaign Title</h3>
                <p className="text-white/90">{brief.campaignTitle || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Objective</h3>
                <p className="text-white/90">{brief.objective || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Target Audience</h3>
                <p className="text-white/90 whitespace-pre-wrap">{brief.targetAudience || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Desired Reaction</h3>
                <p className="text-white/90">{brief.desiredReaction || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Key Message</h3>
                <p className="text-white/90 whitespace-pre-wrap">{brief.keyMessage || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Tone or Style</h3>
                <p className="text-white/90">{brief.tone || 'N/A'}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Deliverables & Channels</h3>
                {deliverables.length > 0 ? (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {deliverables.map((deliverable: string, index: number) => (
                      <span
                        key={index}
                        className="px-3 py-1 text-sm rounded-full bg-white/10 border border-white/20 text-white/55"
                      >
                        {deliverable}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-white/90">N/A</p>
                )}
              </div>

              {/* Inspiration URLs */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Inspiration URLs</h3>
                {brief.inspirationUrls ? (() => {
                  try {
                    const urls = typeof brief.inspirationUrls === 'string' 
                      ? JSON.parse(brief.inspirationUrls) 
                      : brief.inspirationUrls
                    if (Array.isArray(urls) && urls.length > 0) {
                      return (
                        <div className="space-y-3">
                          {urls.map((url: string, index: number) => (
                            <UrlPreview key={index} url={url} />
                          ))}
                        </div>
                      )
                    }
                  } catch (e) {
                    // Invalid JSON, show as text
                    return <p className="text-white/90 whitespace-pre-wrap">{brief.inspirationUrls}</p>
                  }
                  return <p className="text-white/90">N/A</p>
                })() : <p className="text-white/90">N/A</p>}
              </div>

              {/* Brand Assets */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Brand Assets & Guidelines</h3>
                <p className="text-white/90 whitespace-pre-wrap">{brief.brandAssets || 'N/A'}</p>
              </div>

              {/* Constraints */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Constraints & Limitations</h3>
                <p className="text-white/90 whitespace-pre-wrap">{brief.constraints || 'N/A'}</p>
              </div>

              {/* Budget */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Budget</h3>
                <p className="text-white/90">{brief.budget || 'N/A'}</p>
              </div>

              {/* Event Information */}
              {(brief.briefType === 'event' || brief.briefType === 'custom' || brief.numberOfParticipants || brief.eventLocations) && (
                <div className="border-t border-white/10 pt-6">
                  <h3 className="text-lg font-semibold text-white/90 mb-4">Event Information</h3>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white/60 mb-2">Total Number of Participants</h4>
                    <p className="text-white/90">{brief.numberOfParticipants || 'N/A'}</p>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-white/60 mb-3">Event Locations</h4>
                    {brief.eventLocations ? (() => {
                      try {
                        const locations = typeof brief.eventLocations === 'string' 
                          ? JSON.parse(brief.eventLocations) 
                          : brief.eventLocations
                        if (Array.isArray(locations) && locations.length > 0) {
                          return (
                            <div className="space-y-4">
                              {locations.map((location: any, index: number) => (
                                <div key={index} className={`${panelInner} p-4`}>
                                  <h5 className="text-sm font-semibold text-white/55 mb-3">Location {index + 1}</h5>
                                  <div className="space-y-2 text-sm">
                                    <p><span className="text-white/60">Country:</span> <span className="text-white/90">{location.country || 'N/A'}</span></p>
                                    <p><span className="text-white/60">Address:</span> <span className="text-white/90">{location.address || 'N/A'}</span></p>
                                    <p><span className="text-white/60">Start Date:</span> <span className="text-white/90">{(location.dateStart || location.date_start) ? new Date(location.dateStart || location.date_start).toLocaleDateString() : 'N/A'}</span></p>
                                    <p><span className="text-white/60">End Date:</span> <span className="text-white/90">{(location.dateEnd || location.date_end) ? new Date(location.dateEnd || location.date_end).toLocaleDateString() : 'N/A'}</span></p>
                                    <p><span className="text-white/60">Start Time:</span> <span className="text-white/90">{location.timeStart || location.time_start || 'N/A'}</span></p>
                                    <p><span className="text-white/60">End Time:</span> <span className="text-white/90">{location.timeEnd || location.time_end || 'N/A'}</span></p>
                                    <p><span className="text-white/60">Participants:</span> <span className="text-white/90">{location.participants || 'N/A'}</span></p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )
                        }
                      } catch (e) {
                        // Invalid JSON, show as text
                        return <p className="text-white/90 whitespace-pre-wrap">{brief.eventLocations}</p>
                      }
                      return <p className="text-white/90">N/A</p>
                    })() : <p className="text-white/90">N/A</p>}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-white/60 mb-2">Deadline</h3>
                  <p className="text-white/90">
                    {brief.deadline ? new Date(brief.deadline).toLocaleDateString() : 'N/A'}
                  </p>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-white/60 mb-2">Approval Contact</h3>
                  <p className="text-white/90">
                    {brief.approvalContactName || 'N/A'}
                    {brief.approvalContactEmail && (
                      <>
                        <br />
                        <a href={`mailto:${brief.approvalContactEmail}`} className="text-blue-400 hover:underline">
                          {brief.approvalContactEmail}
                        </a>
                      </>
                    )}
                  </p>
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <h3 className="text-sm font-medium text-white/60 mb-2">Additional Notes</h3>
                <p className="text-white/90 whitespace-pre-wrap">{brief.additionalNotes || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Admin Actions Sidebar */}
        <div className="space-y-6">
          {/* Status Update */}
          <div className={`${panel} p-6`}>
            <h3 className="text-sm font-semibold text-white/90 mb-4">Update Status</h3>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className={fieldClass}
            >
              {STATUS_OPTIONS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>

          {/* Assign Manager */}
          <div className={`${panel} p-6`}>
            <h3 className="text-sm font-semibold text-white/90 mb-4">Assign Manager</h3>
            {brief.assignedManager && (
              <div className="mb-4 p-3 bg-white/5 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <User size={16} className="text-white/50" />
                  <span className="font-semibold text-white/90">{brief.assignedManager.name}</span>
                </div>
                <div className="text-sm text-white/70 space-y-1">
                  <p className="flex items-center gap-1">
                    <Mail size={14} className="text-white/50" />
                    {brief.assignedManager.email}
                  </p>
                  {brief.assignedManager.phone && (
                    <p className="flex items-center gap-1">
                      <Phone size={14} className="text-white/50" />
                      {brief.assignedManager.phone}
                    </p>
                  )}
                </div>
              </div>
            )}
            <select
              value={selectedManagerId}
              onChange={(e) => setSelectedManagerId(e.target.value)}
              className={fieldClass}
            >
              <option value="">Select manager</option>
              {managers.map((manager) => (
                <option key={manager.id} value={manager.id}>
                  {manager.name}
                </option>
              ))}
            </select>
          </div>

          {/* Admin Notes */}
          <div className={`${panel} p-6`}>
            <h3 className="text-sm font-semibold text-white/90 mb-4">Admin Notes</h3>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              className={`${fieldClass} h-auto min-h-[5rem] py-2`}
              placeholder="Add internal notes about this brief..."
            />
          </div>

          {/* Single Save Button */}
          <div className={`${panel} p-6`}>
            <button
              onClick={handleSaveAll}
              disabled={saving}
              className={primaryBtn}
            >
              <Save size={20} className="text-white/50" />
              {saving ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

